import React from 'react';
import { Link } from 'react-router-dom';
import MiniContactForm from './MiniContactForm';
import FAQ from './FAQ';
import SEO from './SEO';
import { BLOG_POSTS, BlogTilePreview } from './Blog.jsx';

const LOCATION_FAQ = [
  {
    question: 'How can I request a quote for your pressure washing services?',
    answer: `Requesting a quote is easy! You can reach out to us by calling our dedicated customer service team at (602) 730-0728, or send us an email at coppercanyonpressurewash@gmail.com. One of our friendly representatives will promptly get in touch with you to discuss your specific cleaning needs and provide you with a customized quote tailored to your requirements.`
  },
  {
    question: 'Is pressure washing safe for all surfaces?',
    answer: {
      intro: 'Generally yes, we specialize in cleaning a wide variety of surfaces, including:',
      list: [
        'Concrete driveways and sidewalks',
        'Asphalt driveways',
        'Brick and stone patios, walls, and walkways',
        'Pool decks and tile',
        'Wood decks and fences',
        'Commercial storefronts, parking lots, and buildings'
      ],
      outro: `If you're unsure about a particular surface, just let us know, and we can advise on the best cleaning method.`
    }
  },
  {
    question: 'Will pressure washing damage my plants or landscaping?',
    answer: `We take great care in protecting your landscaping. Our team will cover or avoid delicate plants and outdoor features to prevent damage. We also use environmentally friendly, non-toxic cleaning solutions to ensure the safety of your garden and surrounding area!`
  },
  {
    question: 'Do you offer discounts or promotions?',
    answer: `Yes! We often run promotions or offer discounts for new customers, repeat customers, or bundled services. Be sure to ask about any available deals when you schedule your service.`
  },
  {
    question: "Can pressure washing increase my home's value?",
    answer: `Yes! A clean, well-maintained exterior can greatly enhance your home's curb appeal, which is a key factor in increasing property value. Whether you're selling your home or just maintaining its appearance, pressure washing can make a significant difference.`
  },
  {
    question: 'How often should I have my property pressure washed?',
    answer: {
      intro: 'The frequency of pressure washing depends on various factors like weather, location, and the condition of your surfaces. For example:',
      list: [
        'Driveways and sidewalks typically need cleaning once a year or whenever they become visibly dirty.',
        'Dumpster Pads should be cleaned every month to prevent mold, mildew, and dirt buildup.',
        'Decks and patios should be cleaned annually to maintain their appearance and prevent weathering.'
      ],
      outro: `We recommend a free consultation to assess your property's specific needs and give you a tailored cleaning schedule.`
    }
  }
];

function LocationPage({ city }) {
  // Hardcoded titles and descriptions for each city
  const citySEO = {
    'Phoenix': {
      title: 'Phoenix Pressure Washing Services | Copper Canyon Pressure Wash',
      description: 'Professional pressure washing in Phoenix, AZ. Residential & commercial cleaning, expert technicians, eco-friendly solutions. Get your free quote today!'
    },
    'Scottsdale': {
      title: 'Scottsdale Pressure Washing Services | Copper Canyon Pressure Wash',
      description: 'Professional pressure washing in Scottsdale, AZ. Residential & commercial cleaning, expert technicians, eco-friendly solutions. Get your free quote today!'
    },
    'Mesa': {
      title: 'Mesa Pressure Washing Services | Copper Canyon Pressure Wash',
      description: 'Professional pressure washing in Mesa, AZ. Residential & commercial cleaning, expert technicians, eco-friendly solutions. Get your free quote today!'
    },
    'Tempe': {
      title: 'Tempe Pressure Washing Services | Copper Canyon Pressure Wash',
      description: 'Professional pressure washing in Tempe, AZ. Residential & commercial cleaning, expert technicians, eco-friendly solutions. Get your free quote today!'
    },
    'Gilbert': {
      title: 'Gilbert Pressure Washing Services | Copper Canyon Pressure Wash',
      description: 'Professional pressure washing in Gilbert, AZ. Residential & commercial cleaning, expert technicians, eco-friendly solutions. Get your free quote today!'
    },
    'Paradise Valley': {
      title: 'Paradise Valley Pressure Washing Services | Copper Canyon Pressure Wash',
      description: 'Professional pressure washing in Paradise Valley, AZ. Residential & commercial cleaning, expert technicians, eco-friendly solutions. Get your free quote today!'
    }
  };

  const locationSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    'name': `Copper Canyon Pressure Wash - ${city}`,
    'image': {
      '@type': 'ImageObject',
      'url': [
        'https://coppercanyonpressurewash.com/Copper Canyon Pressure Wash_final logo.avif',
        'https://coppercanyonpressurewash.com/Copper Canyon Pressure Wash_final logo.webp',
        'https://coppercanyonpressurewash.com/Copper Canyon Pressure Wash_final logo.png'
      ]
    },
    'description': `Professional pressure washing services in ${city}, Arizona. Residential and commercial cleaning solutions.`,
    'address': {
      '@type': 'PostalAddress',
      'addressLocality': city,
      'addressRegion': 'AZ',
      'addressCountry': 'US'
    },
    'areaServed': {
      '@type': 'City',
      'name': city
    },
    'url': `https://coppercanyonpressurewash.com/${city.toLowerCase()}-power-washing`,
    'telephone': '+14807890990',
    'priceRange': '$$',
    'openingHoursSpecification': {
      '@type': 'OpeningHoursSpecification',
      'dayOfWeek': [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday'
      ],
      'opens': '08:00',
      'closes': '18:00'
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <SEO 
        title={citySEO[city].title}
        description={citySEO[city].description}
        schema={locationSchema}
        keywords={`pressure washing, power washing, ${city.toLowerCase()}, exterior cleaning, Copper Canyon Pressure Wash, professional, free quote`}
      />
      
      {/* Hero/Banner Image */}
      <section className="w-full h-48 sm:h-64 md:h-80 lg:h-96 mb-8 rounded-lg overflow-hidden bg-gray-200 flex items-center justify-center" aria-label="Location Banner">
        <img 
          src="/Hero_Image.JPG" 
          alt={`Professional pressure washing services in ${city}, Arizona`} 
          className="w-full h-full object-cover object-center" 
        />
      </section>

      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl font-bold text-[#4a3f35] mb-6">{city} Power Washing</h1>
        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4">Professional Pressure Washing in {city}</h2>
        <p className="mb-6 text-gray-700">
          We proudly serve the {city} area with top-quality pressure washing services. Our experienced team 
          uses state-of-the-art equipment and eco-friendly cleaning solutions to deliver exceptional results 
          for both residential and commercial properties.
        </p>

        <h3 className="text-xl font-semibold text-[#4a3f35] mb-2">Our Services in {city}</h3>
        <ul className="list-disc pl-6 mb-6 text-gray-700">
          <li>Residential and commercial power washing</li>
          <li>Driveway, patio, and sidewalk cleaning</li>
          <li>Gutter and window cleaning</li>
          <li>Graffiti and stain removal</li>
        </ul>

        <h3 className="text-xl font-semibold text-[#4a3f35] mb-2">Why Choose Us in {city}?</h3>
        <ul className="list-disc pl-6 mb-8 text-gray-700">
          <li>Experienced local professionals</li>
          <li>Eco-friendly cleaning solutions</li>
          <li>Customer satisfaction guarantee</li>
          <li>Competitive pricing</li>
          <li>Flexible scheduling</li>
        </ul>

        {/* CTA Box */}
        <div className="bg-[#f8f7f3] border-l-4 border-[#b87c4c] p-6 rounded-lg shadow mb-8">
          <h3 className="text-lg font-bold text-[#b87c4c] mb-2">Get a Free Quote in {city}!</h3>
          <p className="mb-4 text-gray-700">Contact us today for a special offer on pressure washing services in {city}.</p>
          <Link 
            to="/contact" 
            className="inline-block px-6 py-2 bg-[#b87c4c] text-white font-medium rounded-lg shadow hover:bg-[#a06940] transition-colors duration-200"
          >
            Get a Free Quote
          </Link>
        </div>
      </div>

      <div className="max-w-2xl mx-auto mt-12">
        <MiniContactForm />
      </div>

      <FAQ items={LOCATION_FAQ} title={`Frequently Asked Questions About Our Services in ${city}`} />
      {/* Featured Blog Post Section */}
      <section className="mt-16 mb-8">
        <h2 className="text-2xl font-bold text-[#b87c4c] mb-6 text-center">Tips & Resources</h2>
        <div className="max-w-xl mx-auto">
          <BlogTilePreview post={BLOG_POSTS.find(post => post.slug === 'cleaning-house-siding-tips')} />
        </div>
      </section>
    </div>
  );
}

export default LocationPage; 